import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import {
    initSafeChargeOrder,
    openSafeChargeOrder,
    setSafeCharge
} from '../../../../../../../api'

import { appendSafeChargeScript } from '../utils/safecharge'

import useCreditPay from '../hooks/credit-pay'

export default Wrapped => {

    return props => {

        const intl = useIntl()

        const { creditOrder, payMethod, onError, trackPayOrder, showNew } = props
        const [instance, setInstance] = useState(null)
        const [scFields, setScFields] = useState(null)
        const global = useSelector(state => state.global)
        const { config } = global

        const {
            pay,
            paying,
            canPay
        } = useCreditPay({
            onError
        })

        const card = useRef()
        const expiry = useRef()
        const cvv = useRef()

        const cardNumberRef = useRef()
        const expiryRef = useRef()
        const cvvRef = useRef()

        const [cardError, setCardError] = useState(null)
        const [expiryError, setExpiryError] = useState(null)
        const [cvvError, setCvvError] = useState(null)

        const [submiting, setSubmiting] = useState(false)


        useEffect(async () => {

            if (creditOrder && cardNumberRef.current && expiryRef.current && cvvRef.current) {
                const {
                    merchantId,
                    merchantSiteId
                } = creditOrder?.account || {}

                const s = await appendSafeChargeScript()

                if (s) {
                    const safechargeInstalce = s({
                        env: config?.env === 'sandbox' ? 'test' : 'prod',
                        merchantId,
                        merchantSiteId
                    })

                    const ScFileds = safechargeInstalce.fields({
                        fonts: [
                            { cssUrl: 'https://fonts.googleapis.com/css?family=Roboto' },
                        ]
                    })

                    setInstance(safechargeInstalce)

                    setScFields(ScFileds)

                    card.current = ScFileds.create('ccNumber', {})
                    card.current.attach(`#${cardNumberRef.current?.id}`)

                    expiry.current = ScFileds.create('ccExpiration', {})
                    expiry.current.attach(`#${expiryRef.current?.id}`)

                    cvv.current = ScFileds.create('ccCvc', {})
                    cvv.current.attach(`#${cvvRef.current?.id}`)

                    card.current.on('change', () => {
                        setCardError(null)
                    })
                }

            }

        }, [creditOrder, showNew])

        const handleSubmit = async e => {
            e.preventDefault()
            setSubmiting(true)

            const couldPay = await canPay()

            if(!couldPay){
                window.location.href = `/order-confirm/${creditOrder?.order?.transactionId}?transactionId=${creditOrder?.order?.transactionId}`
                return
            }


            if(showNew){
                const {
                    order,
                    account
                } = creditOrder
               
                const openResponse = await openSafeChargeOrder({
                    orderId: order?.id,
                    merchantSiteId: account?.merchantSiteId
                })
    
                if (openResponse?.code === 200) {
                    const {
                        openOrderResponse,
                        email,
                        country,
                        ip,
                        billingAddress,
                    } = openResponse?.result
    
                    instance.createPayment({
                        'sessionToken': openOrderResponse?.sessionToken,
                        'merchantId': openOrderResponse?.merchantId,
                        'merchantSiteId': openOrderResponse?.merchantSiteId,
                        'clientUniqueId': openOrderResponse?.clientUniqueId, // optional
                        'paymentOption': card.current,
                        'cardHolderName': billingAddress?.firstName + ' ' + (billingAddress?.lastName || ''),
                        'billingAddress': billingAddress,
                        'deviceDetails': {
                            'ipAddress': ip
                        }
                    }, res => {
                        callBackSafeCharge({ ...res, sessionToken: openOrderResponse?.sessionToken, saveForNext: true })
                    })
    
                } else {
                    onError({
                        message: openResponse?.result
                    })
                    setSubmiting(false)
                }
            }else{
                await pay()
                setSubmiting(false)
            }
            
        }


        const callBackSafeCharge = async res => {
            const {
                transactionId
            } = creditOrder?.order

            const hasFrontError = setErrorState(res)

            if (res?.result !== 'APPROVED') {
                if (!hasFrontError) {
                    const errorMessage = res?.errorCode === 1116 && !res.reason ? intl.formatMessage({ id: 'credit_1116_error', defaultMessage: 'Pay failed . Please change another card or pay method.' }) : res.errorDescription
                    onError({
                        message: errorMessage || 'Network Error.'
                    })
                }
            }

            const response = await setSafeCharge({
                ...res,
                transactionId: creditOrder?.order?.transactionId
            })

            if (response.code === 200) {
                if (res?.result === 'APPROVED') {
                    window.location.href = `/order-confirm/${transactionId}?transactionId=${transactionId}`
                }
            } else {
                onError({
                    message: response?.result || 'Network Error.'
                })
            }

            setSubmiting(false)
        }


        const setErrorState = useCallback(res => {
            let hasError = false
            switch (res?.errorDescription) {
                case 'incomplete_cvc':
                    setCvvError('missing or incomplete CVV/CVC')
                    hasError = true
                    break
                case 'incomplete_date':
                    setExpiryError('missing or incomplete date')
                    hasError = true
                    break
                case 'date_in_past':
                    setExpiryError('date is in the past')
                    hasError = true
                    break
                case 'invalid_expiration_year':
                    setExpiryError('invalid year value')
                    hasError = true
                    break
                case 'incomplete_card_number':
                    setCardError('incomplete or missing card number')
                    hasError = true
                    break
                case 'incorrect_card_number':
                    setCardError('incorrect card number')
                    hasError = true
                    break
                default:
                    break
            }
            return hasError
        }, [])

        return <Wrapped {...props}
            submiting={submiting}
            cardError={cardError}
            cvvError={cvvError}
            expiryError={expiryError}
            handleSubmit={handleSubmit}
            safecharge={instance}
            refs={{
                cardNumberRef,
                expiryRef,
                cvvRef
            }} />
    }
}